import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Button,
  Card,
  Col,
  Form,
  Row,
  Spinner,
  Table,
  Tab,
  Tabs,
} from "react-bootstrap";
import { getAllSiteStorage, SiteStorageSummaryDto } from "@/api/sharepointApi";
import { getGroupOwners, getGroupSite, M365GroupMember } from "@/api/groupApi";
import {
  provisionSiteSelectedAccess,
  SiteSelectedEnvironment,
  SiteSelectedPermission,
  SiteSelectedProvisionResponse,
  getSiteSelectedRequests,
  getSiteSelectedEvents,
  SiteSelectedRequestSummary,
  SiteSelectedEvent,
} from "@/api/siteSelectedApi";

const ENVIRONMENTS: SiteSelectedEnvironment[] = ["Dev", "Test", "Prod"];
const PERMISSIONS: SiteSelectedPermission[] = ["Read", "Write", "Manage", "FullControl"];

const AdminSiteSelectedProvision: React.FC = () => {
  const [sites, setSites] = useState<SiteStorageSummaryDto[]>([]);
  const [siteFilter, setSiteFilter] = useState<string>("");
  const [selectedSiteGuid, setSelectedSiteGuid] = useState<string>("");
  const [siteId, setSiteId] = useState<string>("");
  const [autoFetchingSiteId, setAutoFetchingSiteId] = useState<boolean>(false);
  const [permission, setPermission] = useState<SiteSelectedPermission>("Write");
  const [environments, setEnvironments] = useState<SiteSelectedEnvironment[]>(["Dev", "Test", "Prod"]);
  const [purpose, setPurpose] = useState<string>("");
  const [justification, setJustification] = useState<string>("");
  const [owners, setOwners] = useState<M365GroupMember[]>([]);
  const [selectedOwners, setSelectedOwners] = useState<string[]>([]);
  const [loadingSites, setLoadingSites] = useState<boolean>(true);
  const [loadingOwners, setLoadingOwners] = useState<boolean>(false);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [testMode, setTestMode] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<SiteSelectedProvisionResponse | null>(null);
  const [activeTab, setActiveTab] = useState<"form" | "history">("form");
  const [requests, setRequests] = useState<SiteSelectedRequestSummary[]>([]);
  const [loadingRequests, setLoadingRequests] = useState<boolean>(false);
  const [selectedRequestGuid, setSelectedRequestGuid] = useState<string | null>(null);
  const [events, setEvents] = useState<SiteSelectedEvent[]>([]);
  const [loadingEvents, setLoadingEvents] = useState<boolean>(false);

  // Load sites on mount
  useEffect(() => {
    const load = async () => {
      try {
        const allSites = await getAllSiteStorage();
        const groupConnected = allSites.filter((s) => {
          if (!s.groupId) return false;

          const url = (s.url || "").toLowerCase();
          const title = (s.title || "").toLowerCase();
          const isOneDrive =
            url.includes("-my.sharepoint.com") ||
            url.includes("/personal/") ||
            title.includes("onedrive");

          return !isOneDrive;
        });
        setSites(groupConnected);
      } catch (err: any) {
        setError(err?.response?.data?.error ?? err?.message ?? "Failed to load SharePoint site inventory.");
      } finally {
        setLoadingSites(false);
      }
    };

    load();
  }, []);

  // Load requests for history tab
  useEffect(() => {
    if (activeTab !== "history") return;

    const loadHistory = async () => {
      setLoadingRequests(true);
      try {
        const data = await getSiteSelectedRequests();
        setRequests(data);
      } catch (err: any) {
        setError(err?.response?.data?.error ?? err?.message ?? "Failed to load request history.");
      } finally {
        setLoadingRequests(false);
      }
    };

    loadHistory();
  }, [activeTab]);

  // Load events when a request is selected
  useEffect(() => {
    if (!selectedRequestGuid) {
      setEvents([]);
      return;
    }

    const loadEvts = async () => {
      setLoadingEvents(true);
      try {
        const data = await getSiteSelectedEvents(selectedRequestGuid);
        setEvents(data);
      } catch (err: any) {
        setError(err?.response?.data?.error ?? err?.message ?? "Failed to load request events.");
      } finally {
        setLoadingEvents(false);
      }
    };

    loadEvts();
  }, [selectedRequestGuid]);

  const selectedSite = useMemo(
    () => sites.find((s) => s.guid === selectedSiteGuid) ?? null,
    [sites, selectedSiteGuid]
  );

  const filteredSites = useMemo(() => {
    const q = siteFilter.trim().toLowerCase();
    if (!q) return sites;

    return sites.filter((s) => {
      const title = (s.title || "").toLowerCase();
      const url = (s.url || "").toLowerCase();
      return title.includes(q) || url.includes(q);
    });
  }, [sites, siteFilter]);

  // Load owners and auto-fetch siteId when group selected
  useEffect(() => {
    const loadOwnersAndSiteId = async () => {
      if (!selectedSite?.groupId) {
        setOwners([]);
        setSelectedOwners([]);
        setSiteId("");
        return;
      }

      setLoadingOwners(true);
      setAutoFetchingSiteId(true);
      try {
        const groupOwners = await getGroupOwners(selectedSite.groupId);
        setOwners(groupOwners);
        const selectedIds = groupOwners.map((o) => String(o.id).trim()).filter((v) => v.length > 0);
        setSelectedOwners(selectedIds);

        // Auto-fetch siteId from Graph
        try {
          const siteData = await getGroupSite(selectedSite.groupId);
          if (siteData?.id) {
            setSiteId(siteData.id);
          }
        } catch {
          // Keep form usable; user can still provide siteId manually.
        }
      } catch (err: any) {
        setError(err?.response?.data?.error ?? err?.message ?? "Failed to load group owners.");
      } finally {
        setLoadingOwners(false);
        setAutoFetchingSiteId(false);
      }
    };

    loadOwnersAndSiteId();
  }, [selectedSite?.groupId]);

  const toggleEnvironment = useCallback((env: SiteSelectedEnvironment) => {
    setEnvironments((prev) =>
      prev.includes(env) ? prev.filter((v) => v !== env) : [...prev, env]
    );
  }, []);

  const toggleOwner = useCallback((ownerObjectId: string) => {
    setSelectedOwners((prev) => {
      const ownerStr = String(ownerObjectId).trim();
      if (!ownerStr) return prev;

      const newSelected = prev.includes(ownerStr)
        ? prev.filter((v) => v !== ownerStr)
        : [...prev, ownerStr];

      return newSelected;
    });
  }, []);

  const generatedNames = useMemo(() => {
    const siteTitle = selectedSite?.title ?? "";
    const siteSeg = siteTitle
      .replace(/[^a-zA-Z0-9\s]/g, " ")
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w[0].toUpperCase() + w.slice(1).toLowerCase())
      .join("");

    const purposeSeg = purpose
      .replace(/[^a-zA-Z0-9\s]/g, " ")
      .split(/\s+/)
      .filter(Boolean)
      .map((w) => w[0].toUpperCase() + w.slice(1).toLowerCase())
      .join("");

    return environments.map((env) =>
      purposeSeg
        ? `Spo.${siteSeg}.${env}.${permission}.${purposeSeg}`
        : `Spo.${siteSeg}.${env}.${permission}`
    );
  }, [selectedSite?.title, environments, permission, purpose]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setResult(null);

    if (!selectedSite) {
      setError("Please select a group-connected site.");
      return;
    }

    if (!siteId.trim()) {
      setError("Site ID is required. Use the Graph site id for the selected site.");
      return;
    }

    if (environments.length === 0) {
      setError("Select at least one environment.");
      return;
    }

    if (selectedOwners.length < 3) {
      setError("Select at least 3 owners.");
      return;
    }

    if (!justification.trim()) {
      setError("Justification is required.");
      return;
    }

    setSubmitting(true);
    try {
      const response = await provisionSiteSelectedAccess({
        siteId: siteId.trim(),
        siteUrl: selectedSite.url,
        siteTitle: selectedSite.title,
        groupId: selectedSite.groupId ?? "",
        environments,
        permission,
        purpose: purpose.trim() || undefined,
        justification: justification.trim(),
        ownerObjectIds: selectedOwners
          .map((v) => String(v))
          .filter((v) => v.length > 0),
        test: testMode,
        debug: false,
        debugLevel: 1,
      });

      setResult(response);
    } catch (err: any) {
      const apiMessage =
        err?.response?.data?.failureReason ||
        err?.response?.data?.error ||
        err?.message ||
        "Provisioning failed.";
      setError(apiMessage);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="container-fluid mt-4">
      <h2 className="mb-1">Site.Selected Provisioning</h2>
      <p className="text-muted mb-4">
        Provision governed Site.Selected app registrations with strict rollback and SQL audit logging.
      </p>

      {error && <Alert variant="danger">{error}</Alert>}

      <Tabs
        activeKey={activeTab}
        onSelect={(k) => setActiveTab(k as "form" | "history")}
        className="mb-4"
      >
        <Tab eventKey="form" title="New Request">
          <Card className="mt-3">
            <Card.Header>
              <strong>Provision Request</strong>
            </Card.Header>
            <Card.Body>
              {loadingSites ? (
                <div className="text-muted">Loading site inventory...</div>
              ) : (
                <Form onSubmit={onSubmit}>
                  <Row className="g-3">
                    <Col md={8}>
                      <Form.Group>
                        <Form.Label>Site Storage</Form.Label>
                        <Form.Control
                          className="mb-2"
                          value={siteFilter}
                          onChange={(e) => setSiteFilter(e.target.value)}
                          placeholder="Type to filter sites by title or URL"
                        />
                        <Form.Select
                          value={selectedSiteGuid}
                          onChange={(e) => setSelectedSiteGuid(e.target.value)}
                          required
                        >
                          <option value="">Select a site...</option>
                          {filteredSites.map((s) => (
                            <option key={s.guid} value={s.guid}>
                              {s.title} ({s.url})
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>
                          Graph Site ID
                          {autoFetchingSiteId && (
                            <Spinner animation="border" size="sm" className="ms-2" />
                          )}
                        </Form.Label>
                        <Form.Control
                          value={siteId}
                          onChange={(e) => setSiteId(e.target.value)}
                          placeholder="contoso.sharepoint.com,site-id,web-id"
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col md={4}>
                      <Form.Group>
                        <Form.Label>Permission</Form.Label>
                        <Form.Select
                          value={permission}
                          onChange={(e) =>
                            setPermission(e.target.value as SiteSelectedPermission)
                          }
                        >
                          {PERMISSIONS.map((p) => (
                            <option key={`perm-${p}`} value={p}>
                              {p}
                            </option>
                          ))}
                        </Form.Select>
                      </Form.Group>
                    </Col>
                    <Col md={8}>
                      <Form.Group>
                        <Form.Label>Purpose (optional)</Form.Label>
                        <Form.Control
                          value={purpose}
                          onChange={(e) => setPurpose(e.target.value)}
                          placeholder="UploadContracts"
                        />
                      </Form.Group>
                    </Col>

                    <Col md={12}>
                      <Form.Group>
                        <Form.Label>Justification</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          value={justification}
                          onChange={(e) => setJustification(e.target.value)}
                          required
                        />
                      </Form.Group>
                    </Col>

                    <Col md={12}>
                      <Form.Label>Environments</Form.Label>
                      <div className="d-flex gap-3">
                        {ENVIRONMENTS.map((env) => (
                          <Form.Check
                            key={`env-${env}`}
                            type="checkbox"
                            label={env}
                            checked={environments.includes(env)}
                            onChange={() => toggleEnvironment(env)}
                          />
                        ))}
                      </div>
                    </Col>

                    <Col md={12}>
                      <Form.Label>
                        Owners{" "}
                        {loadingOwners && (
                          <Spinner animation="border" size="sm" className="ms-2" />
                        )}
                      </Form.Label>
                      <div
                        className="border rounded p-3"
                        style={{ maxHeight: 220, overflowY: "auto" }}
                      >
                        {owners.length === 0 && (
                          <div className="text-muted">
                            No owners loaded for selected group.
                          </div>
                        )}
                        {owners.map((owner, idx) => {
                          const ownerIdStr = String(owner.id).trim();
                          const isChecked = selectedOwners.includes(ownerIdStr);
                          return (
                            <Form.Check
                              key={`${ownerIdStr}-${idx}`}
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => toggleOwner(ownerIdStr)}
                              label={`${owner.displayName}${
                                owner.mail ? ` (${owner.mail})` : ""
                              }`}
                            />
                          );
                        })}
                      </div>
                      <div className="small text-muted mt-1">
                        Selected owners: {selectedOwners.length} (minimum 3)
                      </div>
                    </Col>

                    <Col md={12}>
                      <Form.Check
                        type="switch"
                        id="test-mode"
                        checked={testMode}
                        onChange={(e) => setTestMode(e.target.checked)}
                        label="Run in test mode (validate + preview only)"
                      />
                    </Col>

                    <Col md={12}>
                      <Form.Label>Generated Names Preview</Form.Label>
                      <div className="d-flex flex-wrap gap-2">
                        {generatedNames.map((name, idx) => (
                          <Badge key={`name-${idx}`} bg="secondary">
                            {name}
                          </Badge>
                        ))}
                      </div>
                    </Col>

                    <Col md={12}>
                      <Button type="submit" disabled={submitting}>
                        {submitting
                          ? "Submitting..."
                          : testMode
                          ? "Validate Preview"
                          : "Provision"}
                      </Button>
                    </Col>
                  </Row>
                </Form>
              )}

              {result && (
                <Card className="mt-4">
                  <Card.Header>
                    <strong>Result</strong>
                  </Card.Header>
                  <Card.Body>
                    <p className="mb-2">
                      Request: <code>{result.requestGuid}</code>
                    </p>
                    <p className="mb-3">
                      Status:{" "}
                      <Badge bg={result.succeeded ? "success" : "danger"}>
                        {result.status}
                      </Badge>
                      {result.rolledBack && (
                        <Badge bg="warning" text="dark" className="ms-2">
                          Rolled Back
                        </Badge>
                      )}
                    </p>
                    {result.failureReason && (
                      <Alert variant="warning">{result.failureReason}</Alert>
                    )}
                    <Table striped bordered hover size="sm">
                      <thead>
                        <tr>
                          <th>Environment</th>
                          <th>App Name</th>
                          <th>App ID</th>
                          <th>Status</th>
                          <th>Admin Consent</th>
                          <th>Site Grant</th>
                          <th>Links</th>
                        </tr>
                      </thead>
                      <tbody>
                        {result.environments.map((env) => (
                          <tr key={`env-${env.environment}`}>
                            <td>{env.environment}</td>
                            <td className="text-truncate">{env.displayName}</td>
                            <td>
                              <code style={{ fontSize: 10 }}>{env.appId}</code>
                            </td>
                            <td>
                              <Badge
                                bg={
                                  env.status === "Completed"
                                    ? "success"
                                    : env.status === "Failed"
                                    ? "danger"
                                    : "info"
                                }
                              >
                                {env.status}
                              </Badge>
                            </td>
                            <td>{env.adminConsentStatus}</td>
                            <td>{env.siteGrantStatus}</td>
                            <td>
                              {env.entraReviewUrl && (
                                <a
                                  href={env.entraReviewUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-sm btn-outline-primary me-1"
                                >
                                  Entra
                                </a>
                              )}
                              {env.adminConsentUrl && (
                                <a
                                  href={env.adminConsentUrl}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="btn btn-sm btn-outline-primary"
                                >
                                  Consent
                                </a>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </Table>
                  </Card.Body>
                </Card>
              )}
            </Card.Body>
          </Card>
        </Tab>

        <Tab eventKey="history" title="Request History">
          <Card className="mt-3">
            <Card.Header>
              <strong>All Provisioning Requests</strong>
            </Card.Header>
            <Card.Body>
              {loadingRequests ? (
                <div className="text-muted">Loading requests...</div>
              ) : requests.length === 0 ? (
                <div className="text-muted">No requests found.</div>
              ) : (
                <Table striped bordered hover size="sm">
                  <thead>
                    <tr>
                      <th>Request GUID</th>
                      <th>Site</th>
                      <th>Group</th>
                      <th>Permission</th>
                      <th>Status</th>
                      <th>Created By</th>
                      <th>Date</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {requests.map((req) => (
                      <tr key={req.requestGuid.toString()}>
                        <td>
                          <code style={{ fontSize: 10 }}>
                            {req.requestGuid}
                          </code>
                        </td>
                        <td>{req.siteTitle}</td>
                        <td
                          className="text-truncate"
                          style={{ maxWidth: 150 }}
                        >
                          {req.groupId}
                        </td>
                        <td>{req.permission}</td>
                        <td>
                          <Badge
                            bg={
                              req.requestStatus === "Completed"
                                ? "success"
                                : req.requestStatus === "Failed"
                                ? "danger"
                                : req.requestStatus === "TestValidated"
                                ? "info"
                                : "secondary"
                            }
                          >
                            {req.requestStatus}
                          </Badge>
                        </td>
                        <td>{req.createdBy}</td>
                        <td>{new Date(req.createDt).toLocaleString()}</td>
                        <td>
                          <Button
                            size="sm"
                            variant="outline-primary"
                            onClick={() =>
                              setSelectedRequestGuid(
                                req.requestGuid.toString()
                              )
                            }
                          >
                            Events
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              )}
            </Card.Body>
          </Card>

          {selectedRequestGuid && (
            <Card className="mt-4">
              <Card.Header>
                <strong>Events for {selectedRequestGuid}</strong>
                <Button
                  size="sm"
                  variant="outline-secondary"
                  className="float-end"
                  onClick={() => setSelectedRequestGuid(null)}
                >
                  Clear
                </Button>
              </Card.Header>
              <Card.Body>
                {loadingEvents ? (
                  <div className="text-muted">Loading events...</div>
                ) : events.length === 0 ? (
                  <div className="text-muted">No events recorded.</div>
                ) : (
                  <div style={{ maxHeight: 400, overflowY: "auto" }}>
                    {events.map((evt) => (
                      <div key={evt.eventGuid || `${evt.createDt}-${evt.eventType}-${evt.message}`} className="border-bottom pb-2 mb-2">
                        <div className="d-flex justify-content-between align-items-start">
                          <div>
                            <Badge
                              bg={
                                evt.severity === "Error"
                                  ? "danger"
                                  : evt.severity === "Warning"
                                  ? "warning"
                                  : "info"
                              }
                            >
                              {evt.eventType}
                            </Badge>
                            {evt.environment && (
                              <Badge className="ms-1">
                                {evt.environment}
                              </Badge>
                            )}
                          </div>
                          <small className="text-muted">
                            {new Date(evt.createDt).toLocaleString()}
                          </small>
                        </div>
                        <div className="mt-1 small">{evt.message}</div>
                      </div>
                    ))}
                  </div>
                )}
              </Card.Body>
            </Card>
          )}
        </Tab>
      </Tabs>
    </div>
  );
};

export default AdminSiteSelectedProvision;
